import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Text } from '@chakra-ui/react';
import { useRef } from 'react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import {
    AutosaveSnapshot, clearSnapshot, getRecoverySnapshot, startAutosave,
} from '../../utils/autosave';

/**
 * Crash Recovery Host
 *
 * Mounted once by ProjectDialogsHost. On boot it checks whether the previous
 * session left an autosave snapshot (the tab was closed without a clean
 * save) and offers to restore it. It also arms the periodic autosave writer,
 * building the envelope through a deferred import so the I/O layer stays out
 * of the boot render graph.
 */
export function RecoveryHost() {
    const { t } = useTranslation();
    const [snapshot, setSnapshot] = useState<AutosaveSnapshot | null>(null); const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const pending = getRecoverySnapshot();
        if (pending) setSnapshot(pending);
        void import('../../utils/ProjectFileIO')
            .then(({ captureAutosaveSnapshot }) => startAutosave(captureAutosaveSnapshot))
            .catch(() => { /* autosave is best-effort */ });
    }, []);

    const handleRestore = async () => {
        if (!snapshot) return;
        const { restoreFromSnapshot } = await import('../../utils/ProjectFileIO');
        await restoreFromSnapshot(snapshot.envelopeText, snapshot.displayName);
        setSnapshot(null);
    };

    const handleDiscard = () => {
        clearSnapshot();
        setSnapshot(null);
    };

    return (
        <AppDialog
            isOpen={snapshot !== null}
            onClose={handleDiscard}
            title={t('dialogs.ui.recoverUnsavedWork')}
            variant="alert"
            initialFocusRef={cancelRef}
            footer={
                <>
                    <DialogButton ref={cancelRef} variant="secondary" onClick={handleDiscard}>{t('dialogs.ui.discard')}</DialogButton>
                    <DialogButton variant="primary" onClick={handleRestore}>{t('dialogs.ui.restore')}</DialogButton>
                </>
            }
        >
            <Text mb={2}>
                The previous session ended without saving{' '}
                <Text as="span" fontWeight="semibold">{snapshot?.displayName}</Text>.
            </Text>
            <Text fontSize="sm" color="var(--color-text-muted)">
                Autosave snapshot from {snapshot ? new Date(snapshot.savedAt).toLocaleString() : ''}.
            </Text>
        </AppDialog>
    );
}
